import React, { useRef, useState, useEffect } from 'react';
import { SwipeableDrawer, Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { Close, MoreVert, EmojiEmotions } from '@mui/icons-material';
import SendIcon from '../../assets/icons/CTA.svg';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

const CommentsSidebar = ({
    showComments,
    comments,
    newComment,
    setNewComment,
    onSubmit,
    onClose,
    description,
    postUsername
}) => {
    const inputRef = useRef(null);
    const [showFormattingMenu, setShowFormattingMenu] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const formattingMenuRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const isMobile = useMediaQuery('(max-width: 768px)');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
            if (formattingMenuRef.current && !formattingMenuRef.current.contains(event.target)) {
                setShowFormattingMenu(false);
            }
        };

        if (showEmojiPicker || showFormattingMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker, showFormattingMenu]);

    const toggleFormattingMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowFormattingMenu(prev => !prev);
    };

    const toggleEmojiPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowEmojiPicker(prev => !prev);
    };

    const applyFormatting = (format) => {
        const input = inputRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const selectedText = newComment.substring(start, end);
        let newText = '';

        switch (format) {
            case 'bold':
                newText = `<b>${selectedText}</b>`;
                break;
            case 'italic':
                newText = `<i>${selectedText}</i>`;
                break;
            case 'underline':
                newText = `<u>${selectedText}</u>`;
                break;
            case 'strikethrough':
                newText = `<s>${selectedText}</s>`;
                break;
            default:
                return;
        }

        const newContent = newComment.substring(0, start) + newText + newComment.substring(end);
        setNewComment(newContent);
        setShowFormattingMenu(false);
    };

    const handleEmojiSelect = (emoji) => {
        const input = inputRef.current;
        if (input) {
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const newValue = newComment.substring(0, start) + emoji.native + newComment.substring(end);
            setNewComment(newValue);
            
            // Set cursor position after emoji
            setTimeout(() => {
                input.selectionStart = input.selectionEnd = start + emoji.native.length;
                input.focus();
            }, 0);
        }
    };

    // Shared content for both desktop sidebar and mobile bottom sheet
    const commentsContent = (isMobileView = false) => (
        <>
            <div className="comments-list">
                {description && (
                    <div className="comment-card description-card">
                        <div className="comment-avatar">
                            <div className="avatar-circle">
                                {postUsername ? postUsername.charAt(0).toUpperCase() : '?'}
                            </div>
                        </div>
                        <div className="comment-content">
                            <div className="comment-meta">
                                <span className="comment-username">@{postUsername}</span>
                                <span className="description-label">Description</span>
                            </div>
                            <div className="comment-text">{description}</div>
                        </div>
                    </div>
                )}
                {comments.length === 0 && !description ? (
                    <p className="no-comments">No comments yet.</p>
                ) : comments.length === 0 && description ? null : (
                    comments.map(comment => (
                        <div key={comment.id} className="comment-card">
                            <div className="comment-avatar">
                                <div className="avatar-circle">
                                    {comment.username ? comment.username.charAt(0).toUpperCase() : '?'}
                                </div>
                            </div>
                            <div className="comment-content">
                                <div className="comment-meta">
                                    <span className="comment-username">{comment.username}</span>
                                    {comment.timestamp && (
                                        <span className="comment-timestamp">
                                            {comment.timestamp.toDate ? comment.timestamp.toDate().toLocaleString() : ''}
                                        </span>
                                    )}
                                </div>
                                <div className="comment-text">{comment.text}</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <form className="comment-input-form" onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}>
                <div className="comment-input-container">
                    {!isMobileView && (
                        <button 
                            type="button"
                            className="format-button" 
                            onClick={toggleFormattingMenu}
                            aria-label="Formatting options"
                        >
                            <MoreVert sx={{ fontSize: 20 }} />
                        </button>
                    )}
                    <input
                        ref={inputRef}
                        type="text"
                        className="comment-input-box"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                    />
                    {!isMobileView && (
                        <button 
                            type="button"
                            className="emoji-button" 
                            onClick={toggleEmojiPicker}
                            aria-label="Emoji picker"
                        >
                            <EmojiEmotions sx={{ fontSize: 20 }} />
                        </button>
                    )}
                    <button 
                        type="submit"
                        className="send-button" 
                        disabled={!newComment.trim()}
                        aria-label="Send comment"
                    >
                        <img src={SendIcon} alt="Send" />
                    </button>
                    {!isMobileView && showFormattingMenu && (
                        <div className="formatting-menu" ref={formattingMenuRef}>
                            <button type="button" onClick={() => applyFormatting('bold')} className="format-option">
                                <b>B</b>
                            </button>
                            <button type="button" onClick={() => applyFormatting('italic')} className="format-option">
                                <i>I</i>
                            </button>
                            <button type="button" onClick={() => applyFormatting('underline')} className="format-option">
                                <u>U</u>
                            </button>
                            <button type="button" onClick={() => applyFormatting('strikethrough')} className="format-option">
                                <s>S</s>
                            </button>
                        </div>
                    )}
                    {!isMobileView && showEmojiPicker && (
                        <div className="emoji-picker-container" ref={emojiPickerRef}>
                            <Picker
                                data={data}
                                onEmojiSelect={handleEmojiSelect}
                                theme="light"
                                autoFocus={true}
                                emojiSize={20}
                                emojiButtonSize={28}
                                perLine={8}
                            />
                        </div>
                    )}
                </div>
            </form>
        </>
    );

    // Mobile: Bottom sheet drawer
    if (isMobile) {
        return (
            <SwipeableDrawer
                anchor="bottom"
                open={showComments}
                onClose={onClose}
                onOpen={() => {}}
                disableSwipeToOpen
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 20,
                        borderTopRightRadius: 20,
                        maxHeight: '70vh',
                        height: '70vh',
                        pb: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: '100%',
                    overflow: 'hidden'
                }}>
                    {/* Drag handle */}
                    <Box 
                        sx={{ 
                            width: 40, 
                            height: 4, 
                            backgroundColor: '#ddd', 
                            borderRadius: 2, 
                            mx: 'auto', 
                            mt: 1.5,
                            mb: 1,
                            flexShrink: 0
                        }} 
                    />
                    
                    {/* Header */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        px: 2, 
                        pb: 1,
                        borderBottom: '1px solid #eee',
                        flexShrink: 0
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Comments
                        </Typography>
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                    
                    {/* Content */}
                    <Box sx={{ 
                        flex: 1, 
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {commentsContent(true)}
                    </Box>
                </Box>
            </SwipeableDrawer>
        );
    }

    // Desktop: Right sidebar
    return (
        <div className={`comments-sidebar ${showComments ? 'visible' : ''}`}>
            <div className="comments-header">
                <h3 className="comments-title">Comments</h3>
                <button className="close-comments-btn" onClick={onClose} aria-label="Close comments"><Close sx={{ fontSize: 22 }} /></button>
            </div>
            {commentsContent(false)}
        </div>
    );
};

export default CommentsSidebar;
